import moment, { Moment } from "moment";
interface OpeningHour {
  day: string;
  open?: string;
  close?: string;
  closed?: boolean;
}

interface DateTimeValues {
  open: Moment | null;
  close: Moment | null;
  opensAt: string | null;
  closesAt: string | null;
  isOpen: boolean;
  isClosed: boolean;
}

export const quarryDetailsConts = {
  NOTES_ALT: "Click to edit notes",
  EDIT_ALT: "Click to edit quarry details",
  CLOSED_STATUS: "Closed",
  OPEN_STATUS: "Open",
  REDIRECT_TARGET: "_blank",
  REL_VALUE: "noopener,noreferrer",
};

export const getGoogleMapsUrl = (lat, lng) =>
  `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;

const getNextOpenDay = (openingHours: OpeningHour[], startDayOffset: number = 1): OpeningHour | null => {
  const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const currentDayIndex = moment().day();
  
  for (let i = startDayOffset; i < 7; i++) {
    const nextDayIndex = (currentDayIndex + i) % 7;
    const nextDayName = daysOfWeek[nextDayIndex];
    const nextDay = openingHours?.find(item => item?.day === nextDayName);
    
    if (nextDay && !nextDay.closed && nextDay.open && nextDay.close) {
      return nextDay;
    }
  }
  return null;
};

export const getTodaysDateTime = (openingHours: OpeningHour[]): DateTimeValues => {
  const now = moment();
  const today = now.format("dddd");
  
  const todayItem = openingHours?.find((item) => item?.day === today);
  
  if (!todayItem || todayItem.closed || !todayItem.open || !todayItem.close) {
    const nextOpenDay = getNextOpenDay(openingHours, 1);
    
    return {
      open: null,
      close: null,
      opensAt: nextOpenDay 
        ? `Opens ${nextOpenDay.day} at ${moment(nextOpenDay.open, "hh:mm A").format("LT")}`
        : null,
      closesAt: null,
      isOpen: false,
      isClosed: true,
    };
  }
  
  const openTime = moment(todayItem.open, "hh:mm A");
  const closeTime = moment(todayItem.close, "hh:mm A");
  
  const openDateTime = moment().set({
    hour: openTime.hour(),
    minute: openTime.minute(),
    second: 0,
    millisecond: 0
  });
  
  const closeDateTime = moment().set({
    hour: closeTime.hour(),
    minute: closeTime.minute(),
    second: 0,
    millisecond: 0
  });
  
  const isCurrentlyOpen = now.isBetween(openDateTime, closeDateTime, null, '[)');
  
  let opensAt: string | null = null;
  let closesAt: string | null = null;
  
  if (isCurrentlyOpen) {
    closesAt = `Closes at ${closeTime.format("LT")}`;
  } else if (now.isBefore(openDateTime)) {
    opensAt = `Opens at ${openTime.format("LT")}`;
  } else {
    const nextOpenDay = getNextOpenDay(openingHours, 1);
    opensAt = nextOpenDay 
      ? `Opens ${nextOpenDay.day} at ${moment(nextOpenDay.open, "hh:mm A").format("LT")}`
      : null;
  }
  
  return {
    open: openDateTime,
    close: closeDateTime,
    opensAt,
    closesAt,
    isOpen: isCurrentlyOpen,
    isClosed: !isCurrentlyOpen,
  };
};

export const getFormattedTimes = (openingHours: OpeningHour[]) => {
  const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  
  return daysOfWeek.map((day) => {
    const item = openingHours?.find(hour => hour?.day === day);
    
    if (!item) {
      return [day, "Hours not available"];
    }
    
    if (item.closed) {
      return [day, "Closed"];
    }
    
    if (item.open && item.close) {
      const openTime = moment(item.open, "hh:mm A").format("hh:mm a");
      const closeTime = moment(item.close, "hh:mm A").format("hh:mm a");
      return [day, `${openTime} - ${closeTime}`];
    }
    
    return [day, "Hours not available"];
  });
};

export const getIsClosedDay = (openingHours: OpeningHour[]): boolean => {
  const today = moment().format("dddd");
  const todayItem = openingHours?.find((item) => item?.day === today);
  
  return !todayItem || todayItem.closed === true || !todayItem.open || !todayItem.close;
};